import React from 'react';
import { Link } from 'react-router-dom';
import { calculatePace } from '../utils/progressCalculator';
import { calculateBookProgress } from '../utils/bookUtils';
import { getPageScale } from '../utils/pageScale';

const STATUS_TEXT = {
  ahead: (pace) => `${Math.abs(pace.diffDays)}일 앞섬`,
  behind: (pace) => `${Math.abs(pace.diffDays)}일 뒤처짐`,
  ontrack: () => '계획대로',
  overdue: (pace) => `목표일 ${Math.abs(pace.daysLeft)}일 지남`,
  done: () => '완독'
};

/** 상태별 강조 — 손봐야 하는 것(뒤처짐·초과)만 진하게 */
const STATUS_CLASS = {
  ahead: 'text-ink-600 dark:text-ink-300',
  ontrack: 'text-ink-600 dark:text-ink-300',
  behind: 'text-ink-900 dark:text-ink-50 font-semibold',
  overdue: 'text-ink-900 dark:text-ink-50 font-semibold',
  done: 'text-ink-600 dark:text-ink-300'
};

// 본문 회색 (라이트 7.6:1 / 다크 12.9:1) 과 구분점
const MUTED = 'text-ink-600 dark:text-ink-300';
const DIVIDER = 'text-ink-400 dark:text-ink-500';

/**
 * 진행률 바 + 하루 목표 + 예정 대비 앞섬/뒤처짐.
 * 바 위의 세로 눈금이 '오늘까지 읽었어야 할 지점'이라 한눈에 비교된다.
 */
/**
 * @param {React.ReactNode} unitControl 쪽수 옆에 붙일 단위 전환(종이책·전자책). 홈에서만 넘긴다
 */
const PaceLine = ({ book, compact = false, unitControl = null }) => {
  const pace = calculatePace(book);
  const progress = calculateBookProgress(book);
  // 계산은 기준 쪽수로, 보여줄 때만 사용자가 쓰는 단위로 되돌린다
  const scale = getPageScale(book);
  const show = (n) => scale.toDisplay(n) ?? 0;
  const expectedPercent = book.totalPages > 0
    ? Math.min(100, Math.max(0, (pace.expectedPages / book.totalPages) * 100))
    : 0;

  return (
    <div>
      <div className="flex items-center gap-3">
        {/* 눈금은 바 바깥(위쪽)에 그린다 — .liquid-progress 는 overflow-hidden 이라 안에 두면 잘린다 */}
        <div className="relative flex-1">
          <div className="liquid-progress">
            <div className="liquid-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          {pace.hasTarget && !pace.isDone && (
            <span
              className="absolute -top-1.5 h-2 w-px bg-ink-400 dark:bg-ink-500"
              style={{ left: `${expectedPercent}%` }}
              title={`오늘까지 예정: ${show(pace.expectedPages)}쪽`}
            />
          )}
        </div>
        <span className="shrink-0 text-[13px] text-ink-600 dark:text-ink-300 tabular-nums font-sans">
          {show(pace.actualPages)} / {scale.total}쪽
        </span>
        {unitControl}
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px] leading-5 font-sans">
        {!pace.hasTarget ? (
          pace.isDone ? (
            <span className={MUTED}>완독</span>
          ) : (
            <Link
              to={`/book/${book.id}?tab=info`}
              className={`${MUTED} underline underline-offset-2 hover:text-ink-900 dark:hover:text-ink-50`}
            >
              목표일을 정하면 하루 목표를 계산해드려요
            </Link>
          )
        ) : pace.isDone ? (
          <span className={MUTED}>완독</span>
        ) : pace.isOverdue ? (
          /* 목표일이 지나면 '하루 N쪽'이 남은 분량 전부가 되어 의미가 없다.
             숫자 대신 목표를 다시 잡도록 안내한다. */
          <>
            <span className="text-ink-900 dark:text-ink-50 font-semibold tabular-nums">
              목표일 {Math.abs(pace.daysLeft)}일 지남
            </span>
            <span className={DIVIDER}>·</span>
            <span className={`${MUTED} tabular-nums`}>{show(pace.remainingPages)}쪽 남음</span>
            <span className={DIVIDER}>·</span>
            <Link
              to={`/book/${book.id}?tab=info`}
              className={`${MUTED} underline underline-offset-2 hover:text-ink-900 dark:hover:text-ink-50`}
            >
              목표일 다시 잡기
            </Link>
          </>
        ) : (
          <>
            <span className="text-ink-900 dark:text-ink-50 font-semibold tabular-nums">
              하루 {show(pace.pagesPerDay)}쪽
            </span>
            <span className={DIVIDER}>·</span>
            <span className={`${MUTED} tabular-nums`}>
              {pace.todayRemaining > 0
                ? `오늘 ${show(pace.todayRemaining)}쪽 남음`
                : `오늘 목표 달성 (${show(pace.todayRead)}쪽)`}
            </span>
            <span className={DIVIDER}>·</span>
            <span className={`tabular-nums ${STATUS_CLASS[pace.status]}`}>
              {STATUS_TEXT[pace.status]?.(pace)}
              {(pace.status === 'behind' || pace.status === 'ahead') && (
                <span className={`${MUTED} font-normal`}>
                  {' '}({pace.diffPages > 0 ? '+' : ''}{scale.toDisplayDelta(pace.diffPages)}쪽)
                </span>
              )}
            </span>
            {!compact && pace.daysLeft >= 0 && (
              <>
                <span className={DIVIDER}>·</span>
                <span className={`${MUTED} tabular-nums`}>
                  {pace.daysLeft === 0 ? '오늘이 목표일' : `${pace.daysLeft}일 남음`}
                </span>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaceLine;
